"use client";

import { useCallback } from "react";
import { cn } from "@/lib/utils";
import { Box } from "@/components/atoms/Box";
import { Text } from "@/components/atoms/Text";
import { Heading } from "@/components/atoms/Heading";
import { LeadCaptureForm } from "@/components/molecules/LeadCaptureForm";
import type { LeadCaptureSubmission } from "@/components/molecules/LeadCaptureForm";
import type { HeroProps } from "./Hero.props";
import { useGoogleReCaptcha } from "react-google-recaptcha-v3";
import { useSalesforceIdentity } from "@/components/consent/useSalesforceIdentity";
import { submitLeadCaptureForm } from "@/lib/forms/contactFormService";
import { trackFormSubmissionGTM } from "@/lib/analytics";
import { getStoredUTMParams } from "@/lib/utm";
import { SENSITIVE_QUERY_PARAMS } from "@/lib/salesforce/sanitize-page-url";
interface HeroFormContentProps {
  data: HeroProps["data"];
  isDark: boolean;
}
const glassPanelStyle = {
  backgroundColor: "color-mix(in srgb, var(--overlay) 85%, transparent)"
} as const;

// Strip sensitive query params (e.g. ?email= prefill from email blasts, the
// aqt visitor token) before the URL leaves the browser. The server strips the
// same list again before Lead_Source_URL__c is written; the list lives in
// sanitize-page-url.ts, shared with the Data Cloud URL sanitiser.
const sanitizePageUrl = (url: string): string => {
  try {
    const parsed = new URL(url);
    for (const key of [...parsed.searchParams.keys()]) {
      if (SENSITIVE_QUERY_PARAMS.includes(key.toLowerCase())) parsed.searchParams.delete(key);
    }
    return parsed.toString();
  } catch {
    return url;
  }
};

/**
 * Client component for HeroForm's animated inner content.
 * Kept separate so HeroForm can remain a server component (import "server-only").
 *
 * Owns reCAPTCHA, analytics, Salesforce identity, and UTM integrations.
 */
export const HeroFormContent = ({
  data,
  isDark
}: HeroFormContentProps) => {
  const {
    executeRecaptcha
  } = useGoogleReCaptcha();
  const {
    sendIdentityEvent
  } = useSalesforceIdentity();
  const formPurpose = data.formPurpose ?? "Lead Capture Form Submission";
  const handleSubmit = useCallback(async (submission: LeadCaptureSubmission): Promise<{
    success: boolean;
  }> => {
    const utmParams = getStoredUTMParams();

    // Note contains the form purpose context for Pardot comments.
    // Website URL is now mapped to the standard Salesforce Website field.
    const note = `[${submission.formPurpose}]`;

    // Get reCAPTCHA token
    let recaptchaToken: string | undefined;
    if (executeRecaptcha) {
      try {
        recaptchaToken = await executeRecaptcha("lead_capture_form");
      } catch (error) {
        // reCAPTCHA failure — log for diagnostics and let server-side handle it
        console.warn("reCAPTCHA token generation failed in HeroFormContent:", error);
      }
    }
    try {
      const result = await submitLeadCaptureForm({
        data: {
          firstname: submission.firstName,
          lastname: submission.lastName,
          email: submission.email,
          company: submission.company,
          note,
          websiteUrl: submission.websiteUrl || undefined
        },
        recaptchaToken,
        pageUrl: typeof window !== "undefined" ? sanitizePageUrl(window.location.href) : "",
        utmParams: utmParams as Record<string, string>,
        formFillType: data.formFillType || undefined
      });
      if (result.success) {
        sendIdentityEvent(submission.email, "lead_capture_form_submit", {
          firstName: submission.firstName,
          lastName: submission.lastName
        });
        trackFormSubmissionGTM("lead_capture_form", true, utmParams as Record<string, string>);
      } else {
        trackFormSubmissionGTM("lead_capture_form", false, utmParams as Record<string, string>);
      }
      return {
        success: result.success
      };
    } catch {
      trackFormSubmissionGTM("lead_capture_form", false, utmParams as Record<string, string>);
      return {
        success: false
      };
    }
  }, [executeRecaptcha, sendIdentityEvent, data.formFillType]);
  return <Box className="flex flex-col items-center gap-16 lg:flex-row lg:items-start">
      {/* Text column */}
      <Box className="flex flex-col items-center gap-8 text-center lg:items-start lg:text-left lg:w-1/2">
        {data.title && <Heading level={1} className="text-5xl font-bold tracking-tight text-pretty lg:text-7xl leading-condensed">
            <Text className={cn("inline-block", isDark ? "bg-clip-text text-transparent bg-gradient-to-r from-white to-white/60" : "text-foreground")}>
              {data.title}
            </Text>
            {data.titleLine2 && <Text className="block text-primary mt-2 drop-shadow-sm">
                {` ${data.titleLine2}`}
              </Text>}
          </Heading>}

        {data.subtitle && <Text as="p" className={cn("max-w-xl text-xl font-medium lg:text-2xl leading-relaxed", isDark ? "text-white/70" : "text-foreground/70")}>
            {data.subtitle}
          </Text>}
      </Box>

      {/* Glass card with lead capture form */}
      <Box className="w-full lg:w-1/2">
        <Box className="backdrop-blur-md rounded-2xl p-8 lg:p-12 border border-white/10" style={glassPanelStyle}>
          <LeadCaptureForm heading={data.formHeading ?? "Request Your Free Audit"} description={data.formDescription ?? undefined} submitLabel={data.formSubmitLabel ?? "Get Your Free Audit"} formPurpose={formPurpose} successHeading={data.formSuccessHeading ?? "Thank You!"} successMessage={data.formSuccessMessage ?? "We'll be in touch shortly."} errorMessage={data.formErrorMessage ?? "Something went wrong. Please try again."} onSubmit={handleSubmit} className={cn(isDark && "text-white/90")} />
        </Box>
      </Box>
    </Box>;
};